<script setup>
import { useTalkviaBrand } from './useTalkviaBrand';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useEventListener } from '@vueuse/core';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { setColorTheme } from 'dashboard/helper/themeHelper';
import AppearanceSettingsModal from './AppearanceSettingsModal.vue';
import SidebarAccountSwitcher from './SidebarAccountSwitcher.vue';
import SidebarProfileMenu from './SidebarProfileMenu.vue';

defineProps({
  boxed: { type: Boolean, default: false },
});

const emit = defineEmits([
  'showCreateAccountModal',
  'openKeyShortcutModal',
  'customizeNavigation',
]);

const { t } = useI18n();
const { accountScopedRoute } = useAccount();
const notificationMetadata = useMapGetter('notifications/getMeta');
const userAccounts = useMapGetter('getUserAccounts');
const showAppearanceModal = ref(false);

const hasMultipleAccounts = computed(() => userAccounts.value.length > 1);

const isDarkTheme = ref(
  typeof document !== 'undefined' && document.body.classList.contains('dark')
);
const unreadCount = computed(() => {
  const count = notificationMetadata.value?.unreadCount || 0;
  if (!count) return '';
  return count < 100 ? String(count) : '99+';
});

const appearanceLabel = 'Personalizar aparência';
const quickThemeLabel = computed(() =>
  isDarkTheme.value ? 'Ativar tema claro' : 'Ativar tema escuro'
);
const newConversationLabel = 'Nova conversa';

const refreshThemeState = () => {
  isDarkTheme.value = document.body.classList.contains('dark');
};

const toggleColorTheme = () => {
  LocalStorage.set(
    LOCAL_STORAGE_KEYS.COLOR_SCHEME,
    isDarkTheme.value ? 'light' : 'dark'
  );
  setColorTheme(window.matchMedia('(prefers-color-scheme: dark)').matches);
  refreshThemeState();
  window.dispatchEvent(new CustomEvent('appearance-preferences-changed'));
};

useEventListener(window, 'appearance-preferences-changed', refreshThemeState);

const brand = useTalkviaBrand();
</script>

<template>
  <header
    class="col-span-2 hidden h-16 rounded-2xl border border-n-weak bg-n-background shadow-sm md:block"
  >
    <div
      class="mx-auto flex h-full items-center gap-3 px-4"
      :class="boxed ? 'max-w-[1440px]' : 'w-full'"
    >
      <div class="flex shrink-0 items-center gap-3 pr-2">
        <span class="grid size-9 shrink-0 place-items-center">
          <img
            :src="brand.icon"
            alt=""
            class="size-7 object-contain brightness-0 dark:brightness-100"
          />
        </span>
        <span class="min-w-0 leading-tight">
          <strong class="block truncate text-sm font-bold text-n-slate-12">
            {{ brand.name }}
          </strong>
          <small class="block truncate text-[10px] text-n-slate-10">
            {{ brand.tagline }}
          </small>
        </span>
      </div>

      <div v-if="hasMultipleAccounts" class="w-[min(17rem,24vw)] shrink-0">
        <SidebarAccountSwitcher
          class="w-full"
          @show-create-account-modal="emit('showCreateAccountModal')"
        />
      </div>

      <RouterLink
        :to="{ name: 'search' }"
        class="grid size-10 shrink-0 place-items-center rounded-xl text-n-slate-10 transition hover:bg-n-alpha-2 hover:text-n-brand"
        :title="t('COMBOBOX.SEARCH_PLACEHOLDER')"
        :aria-label="t('COMBOBOX.SEARCH_PLACEHOLDER')"
      >
        <span class="i-hugeicons-search-01 size-[1.125rem]" />
      </RouterLink>

      <div class="flex-1" />

      <ComposeConversation align="end">
        <template #trigger="{ isOpen }">
          <Button
            icon="i-hugeicons-bubble-chat-add"
            :label="newConversationLabel"
            color="blue"
            size="md"
            class="!h-10 !rounded-xl"
            :class="isOpen && '!ring-2 !ring-n-brand/20'"
          />
        </template>
      </ComposeConversation>

      <button
        type="button"
        class="grid size-10 shrink-0 place-items-center rounded-xl text-n-slate-10 transition hover:bg-n-alpha-2 hover:text-n-brand"
        :title="quickThemeLabel"
        :aria-label="quickThemeLabel"
        @click="toggleColorTheme"
      >
        <span
          class="size-[1.125rem]"
          :class="isDarkTheme ? 'i-hugeicons-sun-03' : 'i-hugeicons-moon-02'"
        />
      </button>

      <button
        type="button"
        class="grid size-10 shrink-0 place-items-center rounded-xl text-n-slate-10 transition hover:bg-n-alpha-2 hover:text-n-brand"
        :title="appearanceLabel"
        :aria-label="appearanceLabel"
        @click="showAppearanceModal = true"
      >
        <span class="i-hugeicons-paint-board size-[1.125rem]" />
      </button>

      <RouterLink
        :to="accountScopedRoute('notifications_index')"
        class="relative grid size-10 shrink-0 place-items-center rounded-xl text-n-slate-10 transition hover:bg-n-alpha-2 hover:text-n-brand"
        :title="t('SIDEBAR.NOTIFICATIONS')"
        :aria-label="t('SIDEBAR.NOTIFICATIONS')"
      >
        <span class="i-hugeicons-notification-01 size-[1.125rem]" />
        <span
          v-if="unreadCount"
          class="absolute right-0.5 top-0.5 grid min-h-4 min-w-4 place-items-center rounded-full bg-n-ruby-9 px-1 text-[9px] font-semibold leading-none text-white"
        >
          {{ unreadCount }}
        </span>
      </RouterLink>

      <SidebarProfileMenu
        placement="topbar"
        @open-key-shortcut-modal="emit('openKeyShortcutModal')"
        @customize-navigation="emit('customizeNavigation')"
      />
    </div>

    <AppearanceSettingsModal
      :show="showAppearanceModal"
      @close="showAppearanceModal = false"
      @customize-navigation="emit('customizeNavigation')"
    />
  </header>
</template>
